import { useRef, useState } from "react";
import { CheckCircle2, ChevronRight, MapPin, MessageCircle, Phone, Sparkles } from "lucide-react";
import CmsEditableList from "../components/cms/CmsEditableList";
import CmsEditableText from "../components/cms/CmsEditableText";
import CmsEditBar from "../components/cms/CmsEditBar";
import RecaptchaField from "../components/RecaptchaField";
import { useCmsPage } from "../hooks/useCmsPage";
import { api } from "../services/api";

const inputClasses =
  "w-full rounded-2xl border border-[#3991D5]/15 bg-white px-4 py-4 text-slate-900 placeholder:text-slate-400 transition focus:border-[#3991D5] focus:outline-none focus:ring-4 focus:ring-[#70C6FE]/20";

function Contact() {
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", subject: "", message: "" });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const recaptchaRef = useRef(null);
  const fallback = {
    main: {
      eyebrow: "Contact",
      headline: "We're here\nto help",
      intro:
        "Use this page to reach the Bobbin USA team. The layout keeps the same blue-forward visual language as the home page, but reads more refined and structured.",
      responseEyebrow: "Response",
      responseText: "Fast, direct, and built around your project.",
      privacyText:
        "I agree to the privacy policy and understand my details will only be used to respond to this request.",
      submitLabel: "Send message",
    },
  };
  const cms = useCmsPage("contact", fallback);
  const contactCards = cms.json("main", "contactCards", [
    { label: "Phone", value: "+31 6 53 29 29 39", icon: "phone" },
    { label: "Email", value: "info@pieterkoopt.nl", icon: "message" },
    { label: "Address", value: "Johan de Wittstraat 33, Dordrecht", icon: "map" },
  ]);
  const defaultContactIcons = [Phone, MessageCircle, MapPin];
  const iconMap = { phone: Phone, message: MessageCircle, map: MapPin };

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      await api.post("/support/public/contact", {
        name: form.fullName,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
        recaptchaToken,
      });
      setForm({ fullName: "", email: "", phone: "", subject: "", message: "" });
      setRecaptchaToken("");
      recaptchaRef.current?.reset();
      setSubmitStatus({ type: "success", message: "Your message was sent. Our team will review it shortly." });
    } catch (err) {
      setSubmitStatus({ type: "error", message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#eaf6ff] via-white to-[#f8fbff] px-4 py-12 text-slate-900 sm:px-8">
      <CmsEditBar editMode={cms.editMode} pageTitle="Contact Page" />
      <div className="absolute left-[-80px] top-20 h-56 w-56 rounded-full bg-[#70C6FE]/20 blur-3xl" />
      <div className="absolute bottom-0 right-[-70px] h-64 w-64 rounded-full bg-[#EAEBB8]/35 blur-3xl" />

      <div className="container relative z-10">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#3991D5]/15 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#1278b5] shadow-sm">
          <Sparkles className="h-3.5 w-3.5" />
          <CmsEditableText editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="main" value={cms.text("main", "eyebrow", fallback.main.eyebrow)} />
        </div>

        <div className="grid gap-8 rounded-[32px] border border-[#3991D5]/10 bg-white/90 p-6 shadow-[0_18px_60px_rgba(57,145,213,0.12)] backdrop-blur lg:grid-cols-[0.95fr_1.05fr] lg:p-10">
          <div className="flex flex-col justify-between">
            <div>
              <CmsEditableText as="h1" className="font-gopher text-5xl font-black uppercase leading-[0.92] text-[#0f172a] sm:text-6xl" editMode={cms.editMode} fieldKey="headline" multiline onSave={cms.updateField} sectionKey="main" value={cms.text("main", "headline", fallback.main.headline)} />
              <CmsEditableText as="p" className="mt-5 max-w-lg font-pmesans text-base leading-7 text-slate-600 sm:text-lg" editMode={cms.editMode} fieldKey="intro" multiline onSave={cms.updateField} sectionKey="main" value={cms.text("main", "intro", fallback.main.intro)} />
            </div>

            <div className="mt-8 grid gap-3">
              <CmsEditableList editMode={cms.editMode} fieldKey="contactCards" fields={["label", "value"]} itemName="contact box" label="Contact info boxes" onSave={cms.updateField} sectionKey="main" value={contactCards} />
              {contactCards.map((card, index) => {
                const Icon = iconMap[card.icon] || defaultContactIcons[index] || Phone;

                return (
                  <div
                    key={card.label}
                    className="flex items-center gap-4 rounded-2xl border border-[#3991D5]/10 bg-[#f8fbff] p-4"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#3991D5] text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {card.label}
                      </div>
                      <div className="mt-1 font-pmesans text-base text-slate-800">
                        {card.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 rounded-[28px] bg-[#3991D5] p-5 text-white">
              <p className="text-sm uppercase tracking-[0.18em] text-white/75">
                <CmsEditableText editMode={cms.editMode} fieldKey="responseEyebrow" onSave={cms.updateField} sectionKey="main" value={cms.text("main", "responseEyebrow", fallback.main.responseEyebrow)} />
              </p>
              <CmsEditableText as="p" className="mt-2 font-gopher text-2xl font-black uppercase leading-tight" editMode={cms.editMode} fieldKey="responseText" multiline onSave={cms.updateField} sectionKey="main" value={cms.text("main", "responseText", fallback.main.responseText)} />
            </div>
          </div>

          <div className="rounded-[28px] bg-[#f8fbff] p-5 sm:p-6">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="fullName">
                    Full name
                  </label>
                  <input
                    className={inputClasses}
                    id="fullName"
                    name="fullName"
                    onChange={updateForm}
                    placeholder="First and last name"
                    type="text"
                    required
                    value={form.fullName}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">
                    Email address
                  </label>
                  <input
                    className={inputClasses}
                    id="email"
                    name="email"
                    onChange={updateForm}
                    placeholder="example@mail.com"
                    type="email"
                    required
                    value={form.email}
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="phone">
                    Phone number
                  </label>
                  <input
                    className={inputClasses}
                    id="phone"
                    name="phone"
                    onChange={updateForm}
                    placeholder="+31 6 53 29 29 39"
                    type="tel"
                    value={form.phone}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="subject">
                    Subject
                  </label>
                  <input
                    className={inputClasses}
                    id="subject"
                    name="subject"
                    onChange={updateForm}
                    placeholder="How can we help?"
                    type="text"
                    value={form.subject}
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="message">
                  Message
                </label>
                <textarea
                  className={`${inputClasses} min-h-[150px] resize-none`}
                  id="message"
                  name="message"
                  onChange={updateForm}
                  placeholder="Tell us a little about what you need..."
                  required
                  value={form.message}
                />
              </div>

              <label className="flex items-start gap-3 text-sm text-slate-600">
                <input
                  className="mt-1 h-4 w-4 rounded border-[#3991D5]/30 text-[#3991D5] focus:ring-[#3991D5]"
                  name="privacy"
                  type="checkbox"
                  required
                />
                <span>
                  <CmsEditableText editMode={cms.editMode} fieldKey="privacyText" multiline onSave={cms.updateField} sectionKey="main" value={cms.text("main", "privacyText", fallback.main.privacyText)} />
                </span>
              </label>

              <RecaptchaField ref={recaptchaRef} onVerify={setRecaptchaToken} />

              {submitStatus.message && (
                <div className={`rounded-2xl p-4 text-sm font-bold ${submitStatus.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                  {submitStatus.message}
                </div>
              )}

              <button
                className="inline-flex items-center gap-3 rounded-full bg-[#3991D5] px-7 py-4 font-gopher text-sm font-bold uppercase tracking-wide text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                disabled={submitting}
                type="submit"
              >
                {submitting ? "Sending..." : <CmsEditableText editMode={cms.editMode} fieldKey="submitLabel" onSave={cms.updateField} sectionKey="main" value={cms.text("main", "submitLabel", fallback.main.submitLabel)} />}
                {submitStatus.type === "success" ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
